import { devlog, devwarn } from '../../util/utils.ts';
import '../inputs/buttons/as-text-button.ts';
import '../inputs/buttons/as-icon-button.ts';
import styleText from './as-popout-button.css?raw';
import { sendEv } from '../../data/events.ts';

/**
 * # ASPopoutButton Component
 * 
 * A button component that toggles a popout card when clicked.
 * 
 * ## Features
 * - Supports both text and icon buttons
 * - Uses as-text-button or as-icon-button for consistent button styling
 * - Automatically creates and manages an as-popout-card
 * - Places children in the popout card's slot
 * - Configurable button text via the `default-text` attribute (for text buttons)
 * - Configurable localized text via the `dict-key` attribute (for text buttons)
 * - Configurable icon via the `icon-name` or `file-name` attributes (for icon buttons)
 * - Controls popout card placement via the `placement` attribute
 * - Can be shown by default via the `show` attribute
 * - Automatically disables button tooltips when the popout is open to prevent overlap
 * 
 * ## Component Hierarchy
 * ```
 * ASPopoutButton (as-popout-button)
 * └── as-text-button or as-icon-button - The clickable button element
 * └── as-popout-card - The popout card (created internally)
 *     └── slot - For the popout card content (user's children)
 * ```
 * 
 * ## Attributes
 * - `button-type`: Type of button to use ("text" or "icon", default: "text")
 * - `default-text`: Sets the text displayed on the button when button-type="text" (default: "Toggle")
 * - `dict-key`: Sets the localized text key when button-type="text"
 * - `icon-name`: Sets the icon when button-type="icon". A set name from the icon list, passed on as the icon button's `icon-set`
 * - `file-name`: Sets the icon file name when button-type="icon"
 * - `placement`: Controls where the popout card appears relative to the button (default: "right")
 *   - Supported values: "right", "left", "top", "bottom", "bottom-left", "bottom-right", "top-left", "top-right"
 * - `show`: When present, shows the popout card by default
 * - `tooltip-default-text`: Default text content for the tooltip
 * - `tooltip-dict-key`: Localization key for tooltip text
 * - `tooltip-placement`: Position of the tooltip relative to the button (default: "bottom")
 *   - Supported values: "top", "right", "bottom", "left", "bottom-left", "bottom-right", "top-left", "top-right"
 * 
 * ## Events
 * ### Emitted Events
 * - `as-intrn-toggle-visibility`: Dispatched to the associated popout card when the button is clicked
 * 
 * ### Internal Behavior
 * - When the popout opens, the button's tooltips are automatically disabled via the `disable-tooltips` attribute
 * - When the popout closes, the button's tooltips are re-enabled by removing the `disable-tooltips` attribute
 */
export class ASPopoutButton extends HTMLElement {
  static readonly tag = 'as-popout-button' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  private _popoutCard: Element | null = null;
  private _buttonElement: HTMLElement | null = null;
  private _buttonText: string = "Toggle";
  private _buttonType: string = "text";
  private _textKey: string | null = null;
  private _iconName: string | null = null;
  private _file_name: string | null = null;
  private _placement: string = "right";
  private _cardSlot: HTMLSlotElement | null = null;
  private _tooltipDefaultText: string | null = null;
  private _tooltipDictKey: string | null = null;
  private _tooltipPlacement: string = "bottom";

  /**
   * Define the observed attributes for the component
   */
  static get observedAttributes(): string[] {
    return ['default-text', 'dict-key', 'popout-placement', 'show', 'button-type', 'icon-name', 'file-name', 'tooltip-default-text', 'tooltip-dict-key', 'tooltip-placement'];
  }

  constructor() {
    super();
    
    // Create shadow DOM with template content
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      ASPopoutButton._sheet
    ];
    this._render();
  }

  /**
   * Renders the component template with current state
   */
  private _render(): void {
    this.shadowRoot!.innerHTML = /*html*/`
      ${this._renderButton()}
      
      <as-popout-card>
        <slot></slot>
      </as-popout-card>
    `;
  }

  /**
   * Renders the appropriate button based on button-type
   */
  private _renderButton(): string {
    // Build tooltip attributes
    const tooltipAttrs = [];
    if (this._tooltipDefaultText) {
      tooltipAttrs.push(`tooltip-default-text="${this._tooltipDefaultText}"`);
    }
    if (this._tooltipDictKey) {
      tooltipAttrs.push(`tooltip-dict-key="${this._tooltipDictKey}"`);
    }
    if (this._tooltipPlacement) {
      tooltipAttrs.push(`tooltip-placement="${this._tooltipPlacement}"`);
    }
    const tooltipAttrString = tooltipAttrs.join(' ');

    if (this._buttonType === "icon") {
      // Render icon button
      // as-icon-button reads a named icon from `icon-set`.
      const iconNameAttr = this._iconName ? `icon-set="${this._iconName}"` : '';
      const fileNameAttr = this._file_name ? `file-name="${this._file_name}"` : '';
      
      return `<as-icon-button ${iconNameAttr} ${fileNameAttr} ${tooltipAttrString}></as-icon-button>`;
    } else {
      // Render text button (default)
      const defaultTextAttr = this._textKey ? '' : `default-text="${this._buttonText}"`;
      const dictKeyAttr = this._textKey ? `dict-key="${this._textKey}"` : '';
      
      return `<as-text-button ${defaultTextAttr} ${dictKeyAttr} ${tooltipAttrString}></as-text-button>`;
    }
  }

  /**
   * Handles changes to observed attributes
   */
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'default-text':
        this._buttonText = newValue || 'Toggle';
        
        // If the component is already connected and using a text button, update the button text
        if (this._buttonElement && this._buttonType === "text" && !this._textKey) {
          this._buttonElement.setAttribute('default-text', this._buttonText);
        } else if (this._buttonType === "text" && !this._textKey) {
          // Otherwise re-render the component
          this._render();
        }
        break;
      case 'dict-key':
        this._textKey = newValue;
        
        // If the component is already connected and using a text button, update the text key
        if (this._buttonElement && this._buttonType === "text") {
          this._buttonElement.setAttribute('dict-key', this._textKey);
        } else if (this._buttonType === "text") {
          // Otherwise re-render the component
          this._render();
        }
        break;
      case 'button-type':
        this._buttonType = newValue || 'text';
        this._render();
        break;
      case 'icon-name':
        this._iconName = newValue;
        
        // If the component is already connected and using an icon button, update the icon
        if (this._buttonElement && this._buttonType === "icon") {
          if (this._iconName) this._buttonElement.setAttribute('icon-set', this._iconName);
          else this._buttonElement.removeAttribute('icon-set');
        } else if (this._buttonType === "icon") {
          // Otherwise re-render the component
          this._render();
        }
        break;
      case 'file-name':
        this._file_name = newValue;
        
        // If the component is already connected and using an icon button, update the file name
        if (this._buttonElement && this._buttonType === "icon") {
          this._buttonElement.setAttribute('file-name', this._file_name);
        } else if (this._buttonType === "icon") {
          // Otherwise re-render the component
          this._render();
        }
        break;
      case 'tooltip-default-text':
        this._tooltipDefaultText = newValue;
        
        // If the component is already connected, update the tooltip
        if (this._buttonElement) {
          if (this._tooltipDefaultText) {
            this._buttonElement.setAttribute('tooltip-default-text', this._tooltipDefaultText);
          } else {
            this._buttonElement.removeAttribute('tooltip-default-text');
          }
        } else {
          // Otherwise re-render the component
          this._render();
        }
        break;
      case 'tooltip-dict-key':
        this._tooltipDictKey = newValue;
        
        // If the component is already connected, update the tooltip
        if (this._buttonElement) {
          if (this._tooltipDictKey) {
            this._buttonElement.setAttribute('tooltip-dict-key', this._tooltipDictKey);
          } else {
            this._buttonElement.removeAttribute('tooltip-dict-key');
          }
        } else {
          // Otherwise re-render the component
          this._render();
        }
        break;
      case 'tooltip-placement':
        this._tooltipPlacement = newValue || 'bottom';
        
        // If the component is already connected, update the tooltip placement
        if (this._buttonElement) {
          this._buttonElement.setAttribute('tooltip-placement', this._tooltipPlacement);
        } else {
          // Otherwise re-render the component
          this._render();
        }
        break;
      case 'popout-placement':
        // Validate placement value
        const validPlacements = ['right', 'left', 'top', 'bottom', 'bottom-left', 'bottom-right', 'top-left', 'top-right'];
        this._placement = validPlacements.includes(newValue) ? newValue : 'right';
        
        // Update the card's placement attribute
        if (this._popoutCard) {
          this._popoutCard.setAttribute('placement', this._placement);
        }
        break;
      case 'show':
        // Handle the show attribute - just pass it to the card
        if (this._popoutCard) {
          if (this.hasAttribute('show')) {
            this._popoutCard.setAttribute('show', '');
          } else {
            this._popoutCard.removeAttribute('show');
          }
        }
        break;
    }
  }

  /**
   * Finds the associated popout card when the component is connected to the DOM
   * and sets up event listeners
   */
  connectedCallback() {
    // Get the popout card element from shadow DOM
    this._popoutCard = this.shadowRoot!.querySelector('as-popout-card');
    
    // Get the button element and add event listener
    this._buttonElement = this.shadowRoot!.querySelector(this._buttonType === "icon" ? 'as-icon-button' : 'as-text-button');
    this._buttonElement?.addEventListener('as-button-click', this._handleClick);
    
    // Set initial text attribute if it exists and using text button
    if (this.hasAttribute('default-text') && this._buttonType === "text" && !this._textKey) {
      const textAttr = this.getAttribute('default-text');
      if (textAttr && this._buttonElement) {
        this._buttonText = textAttr;
        this._buttonElement.setAttribute('default-text', textAttr);
      }
    }
    
    // Set initial dict-key attribute if it exists and using text button
    if (this.hasAttribute('dict-key') && this._buttonType === "text") {
      const textKeyAttr = this.getAttribute('dict-key');
      if (textKeyAttr && this._buttonElement) {
        this._buttonElement.setAttribute('dict-key', textKeyAttr);
      }
    }
    
    // Set initial icon-name attribute if it exists and using icon button
    if (this.hasAttribute('icon-name') && this._buttonType === "icon") {
      const iconNameAttr = this.getAttribute('icon-name');
      if (iconNameAttr && this._buttonElement) {
        this._buttonElement.setAttribute('icon-set', iconNameAttr);
      }
    }
    
    // Set initial file-name attribute if it exists and using icon button
    if (this.hasAttribute('file-name') && this._buttonType === "icon") {
      const fileNameAttr = this.getAttribute('file-name');
      if (fileNameAttr && this._buttonElement) {
        this._buttonElement.setAttribute('file-name', fileNameAttr);
      }
    }
    
    // Set initial tooltip attributes
    if (this.hasAttribute('tooltip-default-text')) {
      const tooltipDefaultTextAttr = this.getAttribute('tooltip-default-text');
      if (tooltipDefaultTextAttr && this._buttonElement) {
        this._tooltipDefaultText = tooltipDefaultTextAttr;
        this._buttonElement.setAttribute('tooltip-default-text', tooltipDefaultTextAttr);
      }
    }
    
    if (this.hasAttribute('tooltip-dict-key')) {
      const tooltipDictKeyAttr = this.getAttribute('tooltip-dict-key');
      if (tooltipDictKeyAttr && this._buttonElement) {
        this._tooltipDictKey = tooltipDictKeyAttr;
        this._buttonElement.setAttribute('tooltip-dict-key', tooltipDictKeyAttr);
      }
    }
    
    if (this.hasAttribute('tooltip-placement')) {
      const tooltipPlacementAttr = this.getAttribute('tooltip-placement');
      if (tooltipPlacementAttr && this._buttonElement) {
        this._tooltipPlacement = tooltipPlacementAttr;
        this._buttonElement.setAttribute('tooltip-placement', tooltipPlacementAttr);
      }
    }
    
    // Set initial placement attribute if it exists
    if (this.hasAttribute('popout-placement')) {
      const placementAttr = this.getAttribute('popout-placement');
      if (placementAttr) {
        const validPlacements = ['right', 'left', 'top', 'bottom', 'bottom-left', 'bottom-right', 'top-left', 'top-right'];
        this._placement = validPlacements.includes(placementAttr) ? placementAttr : 'right';
        
        // Set the card's placement attribute
        if (this._popoutCard) {
          this._popoutCard.setAttribute('placement', this._placement);
        }
      }
    }
    
    // Set initial show attribute if it exists
    if (this.hasAttribute('show') && this._popoutCard) {
      this._popoutCard.setAttribute('show', '');
    }
    
    // Listen for events from the popout card
    if (this._popoutCard) {
      this._popoutCard.addEventListener('as-intrn-visibility-changed', this._handleVisibilityChanged);
      
      // Add click listeners to prevent closing when clicking the card
      const popoutCardElement = this._popoutCard as HTMLElement;
      popoutCardElement.addEventListener('mousedown', this._handlePopoutCardClick, true);
      popoutCardElement.addEventListener('click', this._handlePopoutCardClick, true);
      
      // Use a MutationObserver to observe the slotted content
      const slot = popoutCardElement.querySelector('slot');
      if (slot) {
        // Track assigned elements to clean up listeners properly
        let previousElements: Element[] = [];
        
        slot.addEventListener('slotchange', () => {
          const assignedElements = (slot as HTMLSlotElement).assignedElements();
          
          // Remove listeners from previous elements that are no longer assigned
          previousElements.forEach(el => {
            if (!assignedElements.includes(el)) {
              el.removeEventListener('mousedown', this._handlePopoutCardClick, true);
              el.removeEventListener('click', this._handlePopoutCardClick, true);
            }
          });
          
          // Add listeners to newly assigned elements
          assignedElements.forEach(el => {
            // First remove any existing listeners to prevent duplicates
            el.removeEventListener('mousedown', this._handlePopoutCardClick, true);
            el.removeEventListener('click', this._handlePopoutCardClick, true);
            
            // Add listeners to capture phase to ensure propagation is stopped early
            el.addEventListener('mousedown', this._handlePopoutCardClick, true);
            el.addEventListener('click', this._handlePopoutCardClick, true);
          });
          
          // Update the previous elements reference
          previousElements = [...assignedElements];
        });
        
        // Trigger slotchange manually to handle initial elements
        if ('slotchange' in HTMLSlotElement.prototype) {
          slot.dispatchEvent(new Event('slotchange'));
        } else {
          // Fallback for browsers without native slotchange event
          const assignedElements = (slot as HTMLSlotElement).assignedElements();
          assignedElements.forEach(el => {
            el.addEventListener('mousedown', this._handlePopoutCardClick, true);
            el.addEventListener('click', this._handlePopoutCardClick, true);
          });
          previousElements = [...assignedElements];
        }
      }
    }
    
    // Get button dimensions for positioning
    const buttonWidth = this._buttonElement!.offsetWidth;  
    const buttonHeight = this._buttonElement!.offsetHeight;

    this.style.setProperty('--button-width', `${buttonWidth}px`);
    this.style.setProperty('--button-height', `${buttonHeight}px`);
    
    // Update the card's CSS variables with button dimensions
    if (this._popoutCard) {
      (this._popoutCard as HTMLElement).style.setProperty('--reference-width', `${buttonWidth}px`);
      (this._popoutCard as HTMLElement).style.setProperty('--reference-height', `${buttonHeight}px`);
    }
  }
  
  /**
   * Handles the as-intrn-visibility-changed event from the popout card
   */
  private _handleVisibilityChanged = (event: CustomEvent) => {
    // Update the button's show attribute to match the card
    if (event.detail.visible) {
      this.setAttribute('show', '');
      // Disable tooltips when popout is open
      if (this._buttonElement) {
        this._buttonElement.setAttribute('disable-tooltips', '');
      }
    } else {
      this.removeAttribute('show');
      // Re-enable tooltips when popout is closed
      if (this._buttonElement) {
        this._buttonElement.removeAttribute('disable-tooltips');
      }
    }
  }
  
  /**
   * Handles click events on the popout card to prevent bubbling
   * This stops clicks within the card from closing the popout
   */
  private _handlePopoutCardClick = (event: Event) => {
    // Don't stop propagation or prevent default - let the event bubble naturally
    // Just mark the event as handled so we can check it in the outside click handler
    (event as any)._handledByPopoutCard = true;
    
    devlog('Marked event from popout card or slotted content as handled');
    
    // Return true to allow the event to continue for any needed internal handling
    return true;
  }
  
  /**
   * Handles click events on the button and dispatches the as-intrn-toggle-visibility event
   */
  private _handleClick = (event: Event) => {
    // Prevent default button behavior
    event.preventDefault();
    devlog('Button clicked');
    
    // Update button dimensions before toggling
    if (this._buttonElement && this._popoutCard) {
      const buttonWidth = this._buttonElement.offsetWidth;
      const buttonHeight = this._buttonElement.offsetHeight;
      
      // Update the card's CSS variables with button dimensions
      (this._popoutCard as HTMLElement).style.setProperty('--reference-width', `${buttonWidth}px`);
      (this._popoutCard as HTMLElement).style.setProperty('--reference-height', `${buttonHeight}px`);
    }
    
    // Dispatch the as-intrn-toggle-visibility event
    if (this._popoutCard) {
      devlog("Dispatching as-intrn-toggle-visibility event");
      
      // Simply dispatch the as-intrn-toggle-visibility event
      sendEv(this._popoutCard, 'as-intrn-toggle-visibility');
    } else devwarn("No popout card found");
  }

  /**
   * Clean up event listeners when the element is removed from the DOM
   */
  disconnectedCallback() {
    this._buttonElement?.removeEventListener('as-button-click', this._handleClick);
    
    if (this._popoutCard) {
      const popoutCardElement = this._popoutCard as HTMLElement;
      
      // Remove event listeners
      this._popoutCard.removeEventListener('as-intrn-visibility-changed', this._handleVisibilityChanged);
      popoutCardElement.removeEventListener('mousedown', this._handlePopoutCardClick, true);
      popoutCardElement.removeEventListener('click', this._handlePopoutCardClick, true);
      
      // Remove event listeners from slotted elements
      const slot = popoutCardElement.querySelector('slot') as HTMLSlotElement;
      if (slot) {
        // Get all assigned elements and remove listeners
        const assignedElements = slot.assignedElements();
        assignedElements.forEach(el => {
          el.removeEventListener('mousedown', this._handlePopoutCardClick, true);
          el.removeEventListener('click', this._handlePopoutCardClick, true);
        });
      }
    }
  }
}

customElements.define(ASPopoutButton.tag, ASPopoutButton);

